import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { type FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import MainSidebar from '~/components/MainSidebar/MainSidebar';
import { ModalType } from '~/components/Modal/interface';
import Modal from '~/components/Modal/Modal';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { useRewardedPromocodesStatus } from '~/hooks/useRewardedPromocodesStatus';
import MobileHeaderLayout from '~/pages/Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '~/pages/Main/MobileHeader/MobileHeaderTitle';
import { mainStore, userStore } from '~/stores';
import { createNameValidation } from '~/utils/validators';

import styles from './PersonalData.module.scss';

type Fields = 'name' | 'e-mail' | 'phone-number' | 'birth-date';

interface FieldData {
  type: Fields;
  title: string;
  defaultValue: string;
  error?: ReactNode;
  placeholder?: string;
}

const formatDate = (date: Nullable<string>) => {
  if (!date) {
    return null;
  }

  const instance = new Date(date);
  return instance.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const PersonalDataVariant1 = observer(() => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const [modal, setModal] = useState<Nullable<FieldData>>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const { openModal } = useModal();

  useRewardedPromocodesStatus();

  const fields = useMemo<Record<Fields, FieldData>>(
    () =>
      ({
        name: {
          type: 'name',
          title: t('yourName'),
          defaultValue: userStore.fullName,
          error: t('pleaseEnterName'),
          placeholder: t('messageReceiverNamePlaceholder'),
        },
        'e-mail': {
          type: 'e-mail',
          title: t('email'),
          defaultValue: userStore.email,
          error: t('pleaseEnterEmail'),
          placeholder: t('messageEmailAddressPlaceholder'),
        },
        'phone-number': {
          type: 'phone-number',
          title: t('phoneNumber'),
          defaultValue: mainStore.prettyPhoneNumber(userStore.phone),
        },
        'birth-date': {
          type: 'birth-date',
          title: t('phrases:yourDateOfBirth'),
          defaultValue: formatDate(userStore.dateOfBirth) ?? '',
        },
      }) as const,
    [
      userStore.fullName,
      userStore.email,
      userStore.phone,
      userStore.dateOfBirth,
      t,
    ],
  );

  const deleteAccount = () => openModal(ModalType.DeleteAccount);

  const getFieldProps = (type: Fields) => {
    if (['phone-number', 'birth-date'].includes(type)) {
      return fields[type];
    }

    return {
      ...fields[type],
      onClick: () => {
        setServerError(null);
        setModal(fields[type]);
      },
    };
  };

  const handleSubmit = async (data: string, type: Fields) => {
    setServerError(null);
    try {
      switch (type) {
        case 'name': {
          const { firstName, middleName, lastName } =
            userStore.splitFullName(data);

          if (firstName) {
            await userStore.updatePersonalData({
              first_name: firstName,
              middle_name: middleName,
              last_name: lastName,
              email: userStore.personalData.email,
            });
          }
          break;
        }
        case 'e-mail': {
          await userStore.updatePersonalData({ email: data });
          break;
        }
      }
      setModal(null);
    } catch (errorKey) {
      if (typeof errorKey === 'string') {
        setServerError(t(errorKey));
      }
    }
  };

  const handleClose = () => {
    setModal(null);
    setServerError(null);
  };

  return (
    <>
      <MobileHeaderLayout
        content={<MobileHeaderTitle text={t('personalData')} />}
      />
      <div
        className={cn('content personal-data', styles.personalData)}
        data-company={company.name}
      >
        <aside className="sidebar">
          <MainSidebar />
        </aside>
        <main className="container">
          {!isMobile && (
            <section className={cn('header', styles.header)}>
              <Icon type="arrow" size={24} className="icon__rotate-180" />
              <div className="title">{t('personalData')}</div>
            </section>
          )}
          <div className="flex w-full flex-col gap-4">
            <PersonalDataField {...getFieldProps('name')} />
            <PersonalDataField {...getFieldProps('e-mail')} />
            <PersonalDataField {...getFieldProps('phone-number')} />
            {company.hasAgeRestriction &&
              userStore.personalData.dateOfBirth && (
                <PersonalDataField {...getFieldProps('birth-date')} />
              )}
          </div>
          <section className={cn('delete-accaunt', styles.delete)}>
            <button className="button _bordered" onClick={deleteAccount}>
              <Icon type="trash" size={24} />
              <div className="btn">{t('deleteAccount')}</div>
            </button>
          </section>
        </main>
      </div>
      {modal && (
        <ChangeDataModal
          {...modal}
          serverError={serverError}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />
      )}
    </>
  );
});

interface PersonalDataFieldProps extends FieldData {
  onClick?(): void;
}

const PersonalDataField = ({
  title,
  defaultValue,
  error,
  onClick,
}: PersonalDataFieldProps) => {
  if (company.is('mcosmetic')) {
    return (
      <div
        onClick={onClick}
        className={cn('w-full', onClick ? 'cursor-pointer' : undefined)}
      >
        <div className="px-2 pb-2">
          {error && !defaultValue ? (
            <Typography.Text100Semibold className="text-system-error">
              {error}
            </Typography.Text100Semibold>
          ) : (
            <Typography.Text100Semibold className="text-text-primary">
              {title}
            </Typography.Text100Semibold>
          )}
        </div>
        <div className="flex h-10 items-center rounded-3 bg-surface-transparent-2 px-4">
          <Typography.Text200Regular className="truncate text-text-primary">
            {defaultValue || <>&nbsp;</>}
          </Typography.Text200Regular>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={cn(styles.field, onClick && styles.action)}
    >
      <div>
        {error && !defaultValue ? (
          <div className={styles.field__error}>{error}</div>
        ) : (
          <>
            <div className={styles.field__label}>{title}</div>
            <div className={cn(styles.field__value, 'phoneNumber')}>
              {defaultValue || <>&nbsp;</>}
            </div>
          </>
        )}
      </div>
      {onClick && <Icon type="chevron" size={24} />}
    </div>
  );
};

interface ChangeDataModalProps extends FieldData {
  serverError?: string | null;
  onSubmit(data: string, type: Fields): void;
  onClose(): void;
}

const ChangeDataModal = ({
  type: _type,
  title,
  defaultValue,
  placeholder,
  serverError,
  onSubmit,
  onClose,
}: ChangeDataModalProps) => {
  const { t } = useTranslation();
  const type = _type as 'name' | 'e-mail';
  const [localServerError, setLocalServerError] = useState<string | null>(null);

  useEffect(() => {
    setLocalServerError(serverError ?? null);
  }, [serverError]);

  const RecieverValidationSchema =
    type === 'name'
      ? z.object({
          name: createNameValidation(t).default(
            type === 'name' ? defaultValue : '',
          ),
        })
      : z.object({
          'e-mail': z
            .string()
            .min(1, { message: t('pleaseEnterEmail') })
            .email({ message: t('incorrectEmail') })
            .default(type === 'e-mail' ? defaultValue : ''),
        });

  type RecieverSchema = z.infer<typeof RecieverValidationSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RecieverSchema>({
    resolver: zodResolver(RecieverValidationSchema),
  });

  useEffect(() => {
    setValue(type, defaultValue);
  }, [defaultValue]);

  const inputRegister = register(type);
  const inputProps = {
    ...inputRegister,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalServerError(null);
      return inputRegister.onChange(e);
    },
  };

  const displayError =
    (errors[type as keyof RecieverSchema] as FieldError)?.message ||
    localServerError;

  return (
    <Modal isShow className={styles.modal}>
      <form
        className={styles.container}
        onSubmit={handleSubmit((data) =>
          onSubmit(data[type as keyof RecieverSchema], type),
        )}
      >
        <div className={styles.header}>
          {title}
          <button className={cn('button', styles.close)} onClick={onClose}>
            <Icon type="close" size={32} />
          </button>
        </div>
        <label className={styles.label}>
          <input
            {...inputProps}
            className={styles.input}
            placeholder={placeholder}
          />
          <button
            className="button _no-padding"
            onClick={() => setValue(type, '')}
            type="button"
          >
            <Icon type="close" size={24} />
          </button>
        </label>
        <div className={styles.error}>{displayError}</div>
        <button type="submit" className={cn('button _primary', styles.button)}>
          {t('apply')}
        </button>
      </form>
    </Modal>
  );
};
